import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Autocomplete, TextField, Box, Typography, InputAdornment, Paper, Skeleton, Drawer, IconButton } from '@mui/material';
import { IconSearch, IconClock, IconX } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import axios from 'axios';
import FAQs from './faq.json';
import { AppConfig } from 'AppConfig';
import { formatReptSource } from 'utils/formatReptSource';
import * as ReportTypes from 'features/frontoffice/actions/types';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';
import routePaths from 'routes/routePaths';

const searchInputSx = (isOpen) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: isOpen ? '16px 16px 0 0' : '16px',
        height: 44,
        backgroundColor: '#FFF',
        paddingLeft: '16px !important',
        paddingRight: '16px',
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        gap: '12px',
        outline: "none"
    },
    '& .MuiInputAdornment-positionStart': {
        marginRight: 0,
    },
    '& .MuiInputBase-input': {
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        height: 'auto',
        fontFamily: 'Noto Sans',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '21px',
        letterSpacing: '0px',
        color: '#111111',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: isOpen ? '1px solid #068DF1' : '1px solid #71767A',
        borderBottomLeftRadius: isOpen ? 0 : '16px',
        borderBottomRightRadius: isOpen ? 0 : '16px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        border: '2px solid #068DF1',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: '2px solid #068DF1',
    },
    '& .MuiInputBase-input::placeholder': {
        color: '#111111',
        opacity: 1,
    },
});

const dropDownPanelSx = {
    borderRadius: '0 0 16px 16px',
    border: '2px solid #068DF1',
    borderTopWidth: 0,
    backgroundColor: '#FFF',
    overflowY: 'auto',
    boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.14)',
    '& .MuiAutocomplete-listbox': {
        maxHeight: '560px',
        padding: '8px 0',
    },
    '& .MuiAutocomplete-option': {
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: '16px !important',
        paddingRight: '16px !important',
        minHeight: '48px !important',
    },
    '& .MuiAutocomplete-option:hover': {
        backgroundColor: '#D8E6FF !important',
    },
    '& .MuiAutocomplete-option[data-focus="true"]': {
        backgroundColor: '#D8E6FF !important',
        color: 'inherit !important',
    },
    '& .MuiAutocomplete-option.Mui-focused': {
        backgroundColor: '#D8E6FF !important',
        color: 'inherit !important',
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: '#D8E6FF !important',
        color: 'inherit !important',
    },
};

const sectionHeaderSx = {
    px: 2,
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Rubik',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '21px',
    letterSpacing: '0.14px',
    color: '#001A47',
};

const menuItemTypographySx = {
    fontFamily: 'Noto Sans',
    fontStyle : 'normal',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '21px',
    height: 'auto',
    flex: '1 0 0',
    letterSpacing: '0px',
    color: '#121A22',
};

const drawerHeaderSx = {
		color: '#000',
		fontFamily: 'Rubik',
		fontSize: '16px',
		fontWeight: 600,
		lineHeight: '24px',
		letterSpacing: '0.16px'
};

const skeletonWidths = ['72%', '58%', '83%', '65%', '78%', '52%', '88%', '61%', '75%', '67%'];

const OPTIONS = Object.freeze({
    NO_HISTORY: 'no-history',
    NO_RESULTS: 'no-results',
    LOADING:    'loading',
    FAQ:        'faq',
    HISTORY:    'history',
    RESULT:     'result',
});

const optionRowSx = (disabled, isSmallHeight) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minHeight: isSmallHeight ? '40px' : '48px',
    px: 2,
    cursor: disabled ? 'default' : 'pointer',
    '&:hover': { backgroundColor: disabled ? 'transparent' : '#D8E6FF' },
});

const renderOptionContent = (option) => {
    if (option._type === OPTIONS.NO_HISTORY) return <Typography sx={menuItemTypographySx}>No recent search history.</Typography>;
    if (option._type === OPTIONS.NO_RESULTS) return <Typography sx={menuItemTypographySx}>No results found</Typography>;
    if (option._type === OPTIONS.LOADING) return <Skeleton variant="text" width={skeletonWidths[option._index]} height={24} />;
    if (option._type === OPTIONS.FAQ) {
			return <>
								<IconSearch size={20} color="#000" style={{ flexShrink: 0 }} />
								<Typography sx={menuItemTypographySx}>{option?.text}</Typography>
						</>;
		}
    if (option._type === OPTIONS.HISTORY) {
			return <>
								<IconClock size={20} color="#000" style={{ flexShrink: 0 }} />
								<Typography sx={menuItemTypographySx}>{option?.displayText}</Typography>
						</>;
		}
    if (option._type === OPTIONS.RESULT) return <Typography sx={menuItemTypographySx}>{option?.displayText || option?.name}</Typography>;
    return null;
};

const GlobalSearchField = ({ currentTenantCd, tabletMobileMode, drawerOpen, onDrawerClose }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchHistory, setSearchHistory] = useState({});
    const tableauServiceUrl = useSelector((state) => state.user.tableauServiceUrl);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const inputRef = useRef(null);

    useEffect(() => {
				if (!isOpen && !(tabletMobileMode && drawerOpen)) return;
        const history = JSON.parse(localStorage.getItem('reportHistory')) || {};
        Object.keys(history).forEach(key => {
            history[key] = (history[key] || []).filter(Boolean);
        });
        localStorage.setItem('reportHistory', JSON.stringify(history));
        setSearchHistory(history);
    }, [isOpen, tabletMobileMode, drawerOpen]);

    const fetchSearchResults = async (query) => {
      const reqPayload = {
        query,
        page: 1,
        size: 10
      }
      try {
        const resp = await axios.post(AppConfig.FO_SEARCH_URL + '/api/search', reqPayload);
        trackClick(trackingTypes.SEARCH, trackingActions.QUERY, query);
        setSearchResults(resp.data.items || []);
      }
      catch (error) {
        console.error('Error fetching search results:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debouncedSearch = useCallback(debounce((query) => fetchSearchResults(query), 500), []);
    
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);
    
    useEffect(() => {
        if (searchQuery.trim().length <= 2) {
            setSearchResults([]);
            setHasSearched(false);
        } else {
            setIsLoading(true);
            setHasSearched(true);
            debouncedSearch(searchQuery.trim());
        }
    }, [searchQuery]);

    const navigateToReport = (report, index) => {
        const detail = JSON.stringify({
            reportCategory: report.category || '',
            reportDesc: report.displayText || report.name,
            clickIndex: index + 1,
            matchRate: report.matchRate,
            searchQuery,
            searchbarType: AppConfig.SEARCH_BAR_TYPES.GLOBAL,
        });
        if (report.id) {
            trackClick(trackingTypes.SEARCH, trackingActions.CLICK, report.id, detail);
        }
        const baseReturnUrl = location.pathname;
        const [workbook, dashboard] = report.reptSource.split('/');
        navigate(`${routePaths.frontOffice}/${currentTenantCd}/view/${workbook}/${dashboard}`);
        dispatch({
            type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
            reportUrl: report.reportSource
                ? report.reportSource
                : formatReptSource(tableauServiceUrl, currentTenantCd, report.reptSource),
            category: report.category || '',
            title: report.displayText || report.name,
            reportBaseUrl: baseReturnUrl,
            report,
        });
        sessionStorage.setItem('reportOrigin', JSON.stringify({ reportBaseUrl: baseReturnUrl }));
				if (tabletMobileMode) onDrawerClose?.();
    }

    const historyForTenant = useMemo(() => (searchHistory[currentTenantCd] || [])
        .filter(
            (report) => report?.category?.toLowerCase() !== 'metrics'
        ),
        [searchHistory, currentTenantCd]
    );

    const options = useMemo(() => {
        const isSearching = searchQuery.trim().length > 2;
        if (isSearching) {
            if (isLoading) return Array.from({ length: 10 }, (_, i) => ({ _type: OPTIONS.LOADING, _index: i }));
            if (hasSearched && !searchResults.length) {
                return [{ _type: OPTIONS.NO_RESULTS }];
            }
            return searchResults.map((r) => ({ ...r, _type: OPTIONS.RESULT }));
        }
        const searchHistoryOpts =
            historyForTenant.length > 0
                ? historyForTenant.map((r) => ({ ...r, _type: OPTIONS.HISTORY, _group: '' }))
                : [{ _type: OPTIONS.NO_HISTORY, _group: '' }];

        const faqOpts = FAQs.map((f) => ({ ...f, _type: OPTIONS.FAQ, _group: 'Suggested' }));
        return [...searchHistoryOpts, ...faqOpts];
    }, [searchQuery, isLoading, hasSearched, searchResults, historyForTenant]);

    const handleChange = (_, value, reason) => {
        if (reason !== 'selectOption' || !value || typeof value === 'string') return;
				handleOptionSelect(value);
    };

    const handleInputChange = (_, value, reason) => {
        if (reason === 'input') {
            setSearchQuery(value);
        } else if (reason === 'clear') {
            setSearchQuery('');
            setSearchResults([]);
            setHasSearched(false);
        }
    };

    const handleClose = (_, reason) => {
        if (reason === 'selectOption' || reason === 'toggleInput') return;
        setIsOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        setHasSearched(false);
    };

		const handleOptionSelect = (option) => {
				if (option._type === OPTIONS.NO_HISTORY || option._type === OPTIONS.NO_RESULTS || option._type === OPTIONS.LOADING) return;
				if (option._type === OPTIONS.FAQ) {
						setSearchQuery(option.text);
				} else {
						navigateToReport(option, options.indexOf(option));
						setIsOpen(false);
						setSearchQuery('');
						setSearchResults([]);
						setHasSearched(false);
				}
		};

		const handleClear = () => {
			setSearchQuery('');
			setSearchResults([]);
			setHasSearched(false);
		};

		const handleDrawerClose = () => {
				handleClear();
				onDrawerClose?.();
		};

		if (tabletMobileMode) {
				let lastGroup = null;
				return (
						<Drawer
								anchor="top"
								open={drawerOpen}
								onClose={handleDrawerClose}
								PaperProps={{ sx: { height: '100%', px: { xs: 0, sm: 4 }, py: {xs: 3, sm: 3.5 } } }}
						>
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, pl: 2, pr: '18px' }}>
										<Typography sx={drawerHeaderSx}>
												Search
										</Typography>
										<IconButton
											aria-label="Close global search drawer view"
											onClick={handleDrawerClose}
											edge="end"
											sx={{
												color: '#1F1F1F',
												'&:hover svg': { color: '#000000' }
											}}
										>
											<IconX size={24} />
										</IconButton>
								</Box>
								<TextField
										fullWidth
										variant="outlined"
										size="small"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										sx={{ ...searchInputSx(false), px: 2 }}
										InputProps={{
												startAdornment: (
														<InputAdornment position="start">
																<IconSearch size={20} color="#001A47" />
														</InputAdornment>
												),
												endAdornment: searchQuery && (
														<InputAdornment position="end">
																<Box
																	component="button"
																	type="button"
																	aria-label="Clear search"
																	onClick={handleClear}
																	sx={{ all: 'unset', display: 'flex' }}
																>
																	<IconX size={20} color="#001A47" />
																</Box>
														</InputAdornment>
												),
										}}
								/>
								<Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1 }}>
										{options.map((option, i) => {
												const disabled = option._type === OPTIONS.NO_HISTORY || option._type === OPTIONS.NO_RESULTS || option._type === OPTIONS.LOADING;
												const showHeader = option._group && option._group !== lastGroup;
												const isSmallHeight = option._type === OPTIONS.NO_HISTORY;
												lastGroup = option._group;
												return (
														<Box component="li" key={i} sx={{ listStyle: 'none' }}>
																{showHeader && <Typography sx={sectionHeaderSx}>{option._group}</Typography>}
																<Box onClick={() => handleOptionSelect(option)} sx={optionRowSx(disabled, isSmallHeight)}>
																		{renderOptionContent(option)}
																</Box>
														</Box>
												);
										})}
								</Box>
						</Drawer>
				);
		}

		// Desktop View
    return (
        <Box display="flex" flexDirection="row" justifyContent="right" alignItems="center">
            <Autocomplete
                freeSolo
                open={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={handleClose}
                value={null}
                inputValue={searchQuery}
                onInputChange={handleInputChange}
                onChange={handleChange}
                options={options}
                filterOptions={(x) => x} // Bypass default filtering
                groupBy={(opt) => opt._group}
                getOptionLabel={(opt) =>
                    typeof opt === 'string' ? opt : opt.text || opt.displayText || opt.name || ''
                }
                isOptionEqualToValue={() => false}
                getOptionDisabled={(opt) =>
                    opt._type === OPTIONS.NO_HISTORY || opt._type === OPTIONS.NO_RESULTS || opt._type === OPTIONS.LOADING
                }
                sx={{ width: 408 }}
                // Dropdown panel
                PaperComponent={({ children, ...props }) => (
                    <Paper {...props} elevation={0} sx={dropDownPanelSx}>
                        {children}
                    </Paper>
                )}
                // Search Input Field
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant='outlined'
                        placeholder={isOpen ? '' : 'Search'}
                        inputRef={inputRef}
                        size="small"
                        sx={searchInputSx(isOpen)}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                            <InputAdornment position="start">
                                <IconSearch size={20} color="#001A47" />
                            </InputAdornment>
                            ),
                        }}
                    />
                )}
                slotProps={{clearIndicator: { title: '' }}}
                clearIcon={<IconX size={20} color="#001A47" />}
                // Dropdown section headers (Suggested)
                renderGroup={(params) => (
                    <Box key={params.key}>
                        {params.group && (
                            <Typography sx={sectionHeaderSx}>
                                {params.group}
                            </Typography>
                        )}
                        <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                            {params.children}
                        </Box>
                    </Box>
                )}
								renderOption={(props, option) => {
										const { key, ...listProps } = props;
										const disabled = option._type === OPTIONS.NO_HISTORY || option._type === OPTIONS.NO_RESULTS || option._type === OPTIONS.LOADING;
										return (
												<Box
														component="li"
														key={key}
														{...listProps}
														sx={{
																display: 'flex',
																alignItems: 'center',
																gap: 1,
																...(disabled && { cursor: 'default', opacity: '1 !important' }),
														}}
												>
														{renderOptionContent(option)}
												</Box>
										);
								}}
            />
        </Box>
    );
};

export default GlobalSearchField;
